import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { ApiError } from '../api/client'
import {
  addWishlistItem,
  fetchWishlist,
  removeWishlistItem,
} from '../api/wishlist'
import type { ApiWishlistItem } from '../api/types'
import { useAuth } from './useAuth'

type WishlistContextValue = {
  items: ApiWishlistItem[]
  productIds: Set<string>
  itemCount: number
  loading: boolean
  error: string | null
  isWishlisted: (productId: string) => boolean
  toggle: (productId: string) => Promise<void>
  remove: (productId: string) => Promise<void>
  refresh: () => Promise<void>
}

const WishlistContext = createContext<WishlistContextValue | null>(null)

export function WishlistProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth()
  const [items, setItems] = useState<ApiWishlistItem[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    if (!isAuthenticated) {
      setItems([])
      setError(null)
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)
    try {
      const next = await fetchWishlist()
      setItems(next)
    } catch (err) {
      setItems([])
      setError(err instanceof ApiError ? err.message : 'Failed to load wishlist')
    } finally {
      setLoading(false)
    }
  }, [isAuthenticated])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const productIds = useMemo(
    () => new Set(items.map((item) => item.productId)),
    [items],
  )

  const remove = useCallback(async (productId: string) => {
    await removeWishlistItem(productId)
    setItems((current) => current.filter((item) => item.productId !== productId))
  }, [])

  const toggle = useCallback(
    async (productId: string) => {
      if (productIds.has(productId)) {
        await remove(productId)
        return
      }
      const added = await addWishlistItem(productId)
      setItems((current) => {
        if (current.some((item) => item.productId === productId)) return current
        return [added, ...current]
      })
    },
    [productIds, remove],
  )

  const value = useMemo<WishlistContextValue>(
    () => ({
      items,
      productIds,
      itemCount: items.length,
      loading,
      error,
      isWishlisted: (productId) => productIds.has(productId),
      toggle,
      remove,
      refresh,
    }),
    [items, productIds, loading, error, toggle, remove, refresh],
  )

  return (
    <WishlistContext.Provider value={value}>{children}</WishlistContext.Provider>
  )
}

export function useWishlist(): WishlistContextValue {
  const value = useContext(WishlistContext)
  if (!value) {
    throw new Error('useWishlist must be used within WishlistProvider')
  }
  return value
}
